import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reference = searchParams.get('reference');
    const trxref = searchParams.get('trxref');
    const status = searchParams.get('status');
    
    if (reference || trxref) {
      verifyPayment(reference || trxref);
    } else if (status === 'cancelled') {
      setStatus('cancelled');
    } else {
      setStatus('error');
      setError('No payment reference found');
    }
  }, [searchParams]);

  const verifyPayment = async (reference) => {
    try {
      setStatus('verifying');
      const response = await api.post(`/payments/verify/${reference}`);
      
      if (response.data.payment.status === 'success') {
        setStatus('success');
        setPaymentData(response.data);
        toast.success('Payment successful! Your subscription has been activated.');
      } else {
        setStatus('failed');
        setError('Payment verification failed');
        toast.error('Payment verification failed.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setError(error.response?.data?.message || 'Failed to verify payment');
      toast.error('Failed to verify payment. Please contact support.');
    }
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <ClockIcon className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600 mb-6">
              Please wait while we verify your payment with Paystack...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your payment has been processed and your subscription is now active.
            </p>
            
            {paymentData && (
              <div className="bg-green-50 p-6 rounded-lg mb-6 text-left">
                <h3 className="font-semibold text-green-900 mb-4">Transaction Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Reference:</span>
                    <span className="text-gray-900 font-mono">{paymentData.payment.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Amount:</span>
                    <span className="text-gray-900">{formatCurrency(paymentData.payment.amount, paymentData.payment.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {paymentData.payment.status}
                    </span>
                  </div>
                  {paymentData.subscription && (
                    <>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Package:</span>
                        <span className="text-gray-900">{paymentData.subscription.packageName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Start Date:</span>
                        <span className="text-gray-900">{formatDate(paymentData.subscription.startDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">End Date:</span>
                        <span className="text-gray-900">{formatDate(paymentData.subscription.endDate)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                View My Subscriptions
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );

      case 'failed':
      case 'error':
        return (
          <div className="text-center">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">
              {error || 'Your payment could not be processed. Please try again or contact support.'}
            </p>
            
            <div className="bg-red-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-red-800">
                <strong>Need help?</strong> If you believe this is an error, please contact our support team with your payment reference.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );

      case 'cancelled':
        return (
          <div className="text-center">
            <XCircleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h2>
            <p className="text-gray-600 mb-6">
              You cancelled the payment process. No charges have been made to your account.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentCallback;
